/**
 * What the conversation SHOWS: a turn list in, the thread, the composer's state and the meter out.
 *
 * Pure: no React, no i18next. Every decision the chat screen makes about a turn lives here, so the
 * components only draw what these answers name: which card is still actionable, which mark is
 * working or arriving, whether the composer may send, whether the meter shows at all.
 */

import type { TrainWithMeModulesAiDtosResponsesAiProposalResult as ProposalDto } from '~/shared/api/generated/model';

import type {
  AiAnswer,
  AiBuildResult,
  AiChangeDecision,
  AiClarifyingQuestion,
  AiConversation,
  AiPlanOutline,
  AiTurn,
  AiUsageMeter,
  AiUsageWindow,
} from './conversation';

/** A finished draft among the conversation's drafts. */
export type DraftVersion = 'only' | 'newest' | 'earlier';

/** Where a proposal stands; see `BuddyBody`'s `proposal`. */
export type ProposalStanding = 'review' | 'applied' | 'replaced' | 'closed';

/**
 * The step a running turn is on, for the screen to word: its step code, and the newest line the
 * server logged, which names a step the code list does not know yet. Nothing about time.
 */
export interface LiveStep {
  readonly step: string | null;
  readonly line: string | null;
}

/**
 * What Buddy reports of its work on a turn (owner, 10 October 2026): while it runs, the ONE line it
 * is on, beside its mark; every step it reported stays folded behind „Pokaż kroki", collapsed by
 * default. Once the turn is over the live line goes, and the fold stays if there was a step.
 */
export interface TurnProgress {
  /** The step the running turn is on; `null` once it is no longer worked on. */
  readonly live: LiveStep | null;
  /** The fold's steps, oldest first: every line the server logged, the whole history. */
  readonly steps: readonly string[];
}

/** One question and its answer in the trainer's answers bundle. */
export interface AnsweredQuestion {
  readonly question: string;
  readonly answer: string;
  /** Written by the trainer rather than picked: drawn in quotation marks. */
  readonly ownWords: boolean;
}

/** What stands inside one Buddy message. */
export type BuddyBody =
  /** A turn still worked on that has produced nothing yet: its progress is all it shows. */
  | { readonly kind: 'thinking' }
  | {
      readonly kind: 'questions';
      readonly intro: string | null;
      readonly questions: readonly AiClarifyingQuestion[];
      /** The server still takes answers to these questions (`openQuestionsTurnId`). */
      readonly open: boolean;
    }
  | {
      readonly kind: 'outline';
      readonly outline: AiPlanOutline;
      /** The server is waiting on this outline's approval (`waitingOutlineTurnId`). */
      readonly open: boolean;
    }
  | {
      readonly kind: 'build';
      readonly result: AiBuildResult | null;
      readonly failure: string | null;
      /** The build stopped without a result: with the check's problems (`failure`), or without them. */
      readonly failed: boolean;
      /** Buddy was switched off while the work waited: nothing to retry now (R44). */
      readonly unavailable: boolean;
      /** A failed build that is still the newest turn offers a retry. */
      readonly retryable: boolean;
      /**
       * Where this draft stands among the conversation's finished drafts (R11): the only one, the
       * newest of several, or an earlier one. Every version stays openable, with its own decisions,
       * until the trainer chooses one; the thread only says which is which.
       */
      readonly version: DraftVersion | null;
      /**
       * „Zbuduj inną wersję" is offered (R11): the newest finished draft, nothing running, no
       * outline waiting, and built from the latest outline. The server builds again only from the
       * latest outline, so a draft whose outline a later one replaced is not offered what it refuses.
       */
      readonly rebuildable: boolean;
      /** The trainer's rating of this draft (R47): `true` helpful, `false` not, `null` unrated. */
      readonly feedback: boolean | null;
      /** The spreadsheet an imported draft came from (R29); `null` for one Buddy built. */
      readonly importedFrom: string | null;
    }
  | {
      readonly kind: 'proposal';
      readonly proposal: ProposalDto;
      /**
       * Where the proposal stands (R15 to R17): `review` while the server waits on it, `applied` once
       * changes of it went into the draft, `replaced` by a later message's proposal (R16), `closed`
       * when the conversation went past it (a newer draft) without either.
       */
      readonly standing: ProposalStanding;
      /** The trainer's latest decision on each change, as the server keeps it. */
      readonly decisions: ReadonlyMap<string, AiChangeDecision>;
      /** The Buddy draft the proposal was made on, or `null` for the builder's own plan. */
      readonly draftTurnId: number | null;
      /**
       * „Inna wersja" is offered (R11): the proposal the server waits on, nothing running. The server
       * asks again on the same copy of the draft, and refuses it once that copy is no longer current.
       */
      readonly anotherVersion: boolean;
      /** The trainer's rating of this proposal (R47): `true` helpful, `false` not, `null` unrated. */
      readonly feedback: boolean | null;
    }
  | { readonly kind: 'text'; readonly text: string }
  /**
   * A turn that failed for a reason the model did not word (the provider, an unreadable reply), or
   * because Buddy was switched off while it waited (`unavailable`, which offers no retry).
   */
  | {
      readonly kind: 'failed';
      readonly retryable: boolean;
      readonly unavailable: boolean;
      /** Buddy ran out of steps before it finished (`ai_step_limit`): said as such, not as an unknown failure. */
      readonly stepLimit: boolean;
    };

export type ThreadItem =
  | { readonly kind: 'trainer'; readonly id: string; readonly text: string }
  | {
      readonly kind: 'answers';
      readonly id: string;
      readonly answers: readonly AnsweredQuestion[];
      /** The trainer's own words sent with the answers, shown after them; `null` when none. */
      readonly ownWords: string | null;
    }
  | {
      readonly kind: 'buddy';
      readonly id: string;
      readonly turnId: number;
      /** Buddy's mark stands only beside a turn it is still working on (`progress.live`). */
      readonly progress: TurnProgress;
      readonly body: BuddyBody;
    };

/**
 * A turn the server is still working on: the ONE rule for what the thread draws as working, what
 * the composer waits for, and what the poller keeps asking about. A status this build does not
 * know reads as `running` (`fromWire.ts`), so it lands here and is polled rather than dropped.
 */
export function isTurnInFlight(turn: AiTurn): boolean {
  return turn.status === 'queued' || turn.status === 'running';
}

/** The step a turn is on while it is worked on; `null` once it is not. */
function liveStep(turn: AiTurn): LiveStep | null {
  return isTurnInFlight(turn) ? { step: turn.step, line: turn.log.at(-1) ?? null } : null;
}

/** Each answer read against the questions it answers, in the questions' own order. */
function answeredQuestions(
  questions: readonly AiClarifyingQuestion[],
  answers: readonly AiAnswer[],
): AnsweredQuestion[] {
  return questions.flatMap((question): AnsweredQuestion[] => {
    const answer = answers.find((candidate) => candidate.questionId === question.id);
    if (answer === undefined) return [];
    if (answer.kind === 'text') return [{ question: question.text, answer: answer.text, ownWords: true }];
    const option = question.options.find((candidate) => candidate.id === answer.optionId);
    return option === undefined ? [] : [{ question: question.text, answer: option.label, ownWords: false }];
  });
}

interface Standing {
  /** The builds that finished with a draft, oldest first. */
  readonly draftIds: readonly number[];
  /** Some turn is still being worked on. */
  readonly busy: boolean;
  /** The newest outline that arrived and was not dismissed: the one a build may be repeated from. */
  readonly latestOutlineId: number | null;
  readonly waitingOutlineTurnId: number | null;
  readonly openQuestionsTurnId: number | null;
  readonly waitingProposalTurnId: number | null;
  /** The proposals a later message's proposal replaced (R16). */
  readonly replacedProposalIds: ReadonlySet<number>;
}

/** A turn that makes a draft: Buddy's build, or a spreadsheet's import (R29). */
function makesDraft(turn: AiTurn): boolean {
  return turn.kind === 'build' || turn.kind === 'import';
}

function proposalStanding(turn: AiTurn, standing: Standing): ProposalStanding {
  if (standing.replacedProposalIds.has(turn.id)) return 'replaced';
  if ([...turn.decisions.values()].includes('applied')) return 'applied';
  return standing.waitingProposalTurnId === turn.id ? 'review' : 'closed';
}

function draftVersion(turnId: number, draftIds: readonly number[]): DraftVersion {
  if (draftIds.at(-1) !== turnId) return 'earlier';
  return draftIds.length === 1 ? 'only' : 'newest';
}

function buddyBody(turn: AiTurn, newest: boolean, standing: Standing): BuddyBody | null {
  if (makesDraft(turn) && turn.status !== 'refused') {
    const result = turn.result?.kind === 'build' ? turn.result.result : null;
    const drafted = turn.status === 'done' && result !== null;
    return {
      kind: 'build',
      result: turn.status === 'done' ? result : null,
      failure: turn.status === 'failed' ? turn.failure : null,
      failed: turn.status === 'failed',
      unavailable: turn.failureCode === 'ai_unavailable',
      // An import is not asked again: the file it read is not kept (R30).
      retryable: turn.status === 'failed' && newest && turn.failureCode !== 'ai_unavailable' && turn.kind !== 'import',
      version: drafted ? draftVersion(turn.id, standing.draftIds) : null,
      rebuildable:
        drafted &&
        standing.draftIds.at(-1) === turn.id &&
        !standing.busy &&
        standing.waitingOutlineTurnId === null &&
        turn.parentTurnId !== null &&
        turn.parentTurnId === standing.latestOutlineId,
      feedback: turn.feedback,
      importedFrom: turn.input.kind === 'import' ? turn.input.fileName : null,
    };
  }
  if (isTurnInFlight(turn)) return { kind: 'thinking' };
  if (turn.status === 'failed' || turn.result === null) {
    const unavailable = turn.failureCode === 'ai_unavailable';
    return { kind: 'failed', retryable: newest && !unavailable, unavailable, stepLimit: turn.failureCode === 'ai_step_limit' };
  }
  const { result } = turn;
  switch (result.kind) {
    case 'questions':
      // No questions left to ask: the outline is already queued behind this turn, and the turn says
      // only what its sentence says, if anything.
      if (result.questions.length === 0) return result.intro === null ? null : { kind: 'text', text: result.intro };
      return {
        kind: 'questions',
        intro: result.intro,
        questions: result.questions,
        open: standing.openQuestionsTurnId === turn.id,
      };
    case 'outline':
      return { kind: 'outline', outline: result.outline, open: standing.waitingOutlineTurnId === turn.id };
    case 'answer':
      return { kind: 'text', text: result.text };
    case 'refusal':
      return { kind: 'text', text: result.text };
    case 'proposal':
      return {
        kind: 'proposal',
        proposal: result.proposal,
        standing: proposalStanding(turn, standing),
        decisions: turn.decisions,
        draftTurnId: turn.draftTurnId,
        anotherVersion: standing.waitingProposalTurnId === turn.id && !standing.replacedProposalIds.has(turn.id) && !standing.busy,
        feedback: turn.feedback,
      };
    case 'build':
      return { kind: 'failed', retryable: newest, unavailable: false, stepLimit: false };
  }
}

/**
 * The thread, oldest first: what the trainer sent, then Buddy's reply, turn by turn.
 *
 * The answers bundle is read against the questions of the turn that asked them. Skipping the
 * questions, approving the outline and a retry are button presses, not messages, so they add no
 * trainer bubble. A stopped turn keeps what the trainer sent and draws no reply (a dismissed outline
 * stays, closed), and the turn before it is the newest again. Buddy's mark stands only beside the
 * turn still worked on; a finished message carries none (owner, 10 October 2026).
 *
 * Which outline may be approved and which questions may be answered is the server's word
 * (`waitingOutlineTurnId`, `openQuestionsTurnId`), not the turn order's: a question asked while an
 * outline waits leaves that outline's card live.
 */
export function threadItems(
  conversation: Pick<AiConversation, 'turns' | 'waitingOutlineTurnId' | 'openQuestionsTurnId' | 'waitingProposalTurnId'>,
): ThreadItem[] {
  const { turns } = conversation;
  let askedQuestions: readonly AiClarifyingQuestion[] = [];
  const newestId = newestLiveTurn(turns)?.id ?? null;
  const standing: Standing = {
    draftIds: turns
      .filter((turn) => makesDraft(turn) && turn.status === 'done' && turn.result?.kind === 'build')
      .map((turn) => turn.id),
    busy: turns.some(isTurnInFlight),
    latestOutlineId: turns.filter((turn) => turn.status === 'done' && turn.result?.kind === 'outline').at(-1)?.id ?? null,
    waitingOutlineTurnId: conversation.waitingOutlineTurnId,
    openQuestionsTurnId: conversation.openQuestionsTurnId,
    waitingProposalTurnId: conversation.waitingProposalTurnId,
    // The proposal a message revised, or the one the server says a proposal replaced: another
    // version asked for (R11) repeats the input, so only the result names what it replaces.
    replacedProposalIds: new Set(
      turns.flatMap((turn) =>
        turn.status === 'done' && turn.result?.kind === 'proposal'
          ? [turn.revisesTurnId, turn.result.proposal.replacesTurnId ?? null].filter((id): id is number => id !== null)
          : [],
      ),
    ),
  };
  return turns.flatMap((turn): ThreadItem[] => {
    const newest = turn.id === newestId;
    const items: ThreadItem[] = [];
    if (turn.input.kind === 'message') {
      items.push({ kind: 'trainer', id: `${String(turn.id)}:in`, text: turn.input.text });
    } else if (turn.input.kind === 'answers') {
      items.push({
        kind: 'answers',
        id: `${String(turn.id)}:in`,
        answers: answeredQuestions(askedQuestions, turn.input.answers),
        ownWords: turn.input.text,
      });
    }
    if (turn.status === 'done' && turn.result?.kind === 'questions' && turn.result.questions.length > 0) {
      askedQuestions = turn.result.questions;
    }
    const body = turn.status === 'cancelled' ? dismissedBody(turn) : buddyBody(turn, newest, standing);
    if (body !== null) {
      items.push({
        kind: 'buddy',
        id: `${String(turn.id)}:out`,
        turnId: turn.id,
        progress: { live: liveStep(turn), steps: turn.log },
        body,
      });
    }
    return items;
  });
}

/**
 * What a cancelled turn still shows. A turn stopped while it worked produced nothing and shows
 * nothing; an outline the trainer dismissed (R6) had already arrived, and stays in the thread as
 * what was proposed, never approvable again.
 */
function dismissedBody(turn: AiTurn): BuddyBody | null {
  return turn.result?.kind === 'outline' ? { kind: 'outline', outline: turn.result.outline, open: false } : null;
}

/** The newest turn that was not stopped: the one a failure is retried on. */
function newestLiveTurn(turns: readonly AiTurn[]): AiTurn | undefined {
  return turns.filter((turn) => turn.status !== 'cancelled').at(-1);
}

export type ComposerPhase = 'ready' | 'working' | 'blocked';

export interface ComposerState {
  readonly phase: ComposerPhase;
  /** The step the running turn is on: the working composer's status line, the same as the thread's. */
  readonly live: LiveStep | null;
  /** The queued or running turn, which „Zatrzymaj" stops. */
  readonly activeTurnId: number | null;
  /** Whether the conversation has begun: the placeholder changes once it has. */
  readonly started: boolean;
  /** The questions a composer message answers in the trainer's own words, while they are open. */
  readonly openQuestions: readonly AiClarifyingQuestion[] | null;
  /** The turn those open questions belong to: the key the picks on its card are held under. */
  readonly openQuestionsTurnId: number | null;
}

/**
 * What the composer may do. A reached limit wins over running work: the turn already reserved its
 * usage and finishes, but nothing new can be sent until the window frees up (R39, R42).
 */
export function composerState(conversation: AiConversation): ComposerState {
  const { turns, meter } = conversation;
  const running = turns.find(isTurnInFlight) ?? null;
  const asking = turns.find((turn) => turn.id === conversation.openQuestionsTurnId);
  const openQuestions =
    asking?.status === 'done' && asking.result?.kind === 'questions' && asking.result.questions.length > 0
      ? asking.result.questions
      : null;
  return {
    phase: meter.blocked ? 'blocked' : running !== null ? 'working' : 'ready',
    live: running === null ? null : liveStep(running),
    activeTurnId: running?.id ?? null,
    openQuestionsTurnId: openQuestions === null ? null : (asking?.id ?? null),
    started: turns.length > 0,
    openQuestions,
  };
}

/** The trainer's picks on the open questions card, keyed by question. */
export type QuestionPicks = Readonly<Record<string, string>>;

/** „Odpowiedz" is live only when every question has a pick (R6). */
export function everyQuestionPicked(questions: readonly AiClarifyingQuestion[], picks: QuestionPicks): boolean {
  return questions.every((question) => question.options.some((option) => option.id === picks[question.id]));
}

/**
 * The answers a send carries while questions are open: each pick as an option, and the trainer's
 * own words for every question left without one (R6: an answer by picking or in their own words).
 * `null` when there is nothing to send: no own words and a question still unanswered.
 */
export function answersToSend(
  questions: readonly AiClarifyingQuestion[],
  picks: QuestionPicks,
  ownWords: string,
): AiAnswer[] | null {
  const text = ownWords.trim();
  const answers: AiAnswer[] = [];
  for (const question of questions) {
    const optionId = picks[question.id];
    if (optionId !== undefined && question.options.some((option) => option.id === optionId)) {
      answers.push({ questionId: question.id, kind: 'option', optionId });
    } else if (text !== '') {
      answers.push({ questionId: question.id, kind: 'text', text });
    } else {
      return null;
    }
  }
  return answers;
}

/** The limit meter as the composer's foot draws it, or `null` while it stays hidden (R43). */
export interface MeterView {
  readonly percent: number;
  readonly window: AiUsageWindow;
  readonly resetsAt: string | null;
  readonly blocked: boolean;
}

/** The meter shows from 80% of the fuller window, and always once a window is reached (R43). */
export const METER_VISIBLE_FROM_PERCENT = 80;

export function meterView(meter: AiUsageMeter): MeterView | null {
  if (!meter.blocked && meter.percent < METER_VISIBLE_FROM_PERCENT) return null;
  return {
    percent: meter.blocked ? 100 : Math.min(meter.percent, 100),
    window: meter.window,
    resetsAt: meter.resetsAt,
    blocked: meter.blocked,
  };
}

/** One exercise decision on its way to the server: each draft version holds its own. */
export function decisionKey(turnId: number, name: string): string {
  return `${String(turnId)}:${name}`;
}

/** The names still waiting for a decision before the draft may open (R8). */
export function undecidedExercises(result: AiBuildResult): readonly string[] {
  return result.unresolved.filter((exercise) => exercise.decision === null).map((exercise) => exercise.name);
}

/** One run of model text: plain, or inside `**…**`. */
export interface MessageSegment {
  readonly text: string;
  readonly strong: boolean;
}

/**
 * Model text as plain segments (R33). `**bold**` is the one formatting honoured; an unpaired `**`
 * stays literal, and nothing else (a link, an image, markup) is ever interpreted.
 */
export function messageSegments(text: string): MessageSegment[] {
  const parts = text.split('**');
  // An even count of parts means one `**` has no partner: the last one is literal text.
  const paired = parts.length % 2 === 1 ? parts : [...parts.slice(0, -2), `${parts.at(-2) ?? ''}**${parts.at(-1) ?? ''}`];
  return paired
    .map((part, index) => ({ text: part, strong: index % 2 === 1 }))
    .filter((segment) => segment.text !== '');
}

/** When the limit frees up, as the trainer reads it: a clock time today, or a weekday and a time. */
export interface ResetMoment {
  /** Later the same local calendar day as `now`. */
  readonly today: boolean;
  /** ISO weekday of the reset, 1 (Monday) to 7 (Sunday), in the browser's zone. */
  readonly isoWeekday: number;
  readonly at: Date;
}

/** `resetsAt` read in the browser's zone against `now` (R43: the time the trainer can continue). */
export function resetMoment(resetsAt: string, now: Date): ResetMoment {
  const at = new Date(resetsAt);
  const today =
    at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  return { today, isoWeekday: at.getDay() === 0 ? 7 : at.getDay(), at };
}
